/**
 * Validación del certificado digital en el SII, en lenguaje de cliente.
 *
 * Contrato del backend: LemuBackend `docs/certificado-validacion.md`.
 * - `POST cliente/certificado-digital/<c>/validar/` → `{ok, codigo, mensaje, reintentable, permisos, detalle}`
 *   (200 con resultado; 409 / 503 sin resultado; 429 por el tope del portal, 5 por hora).
 * - `GET cliente/obtener-credenciales-sii/<c>/` → `success.certificado` con `estado`, `vence`,
 *   `vence_pronto`, `ultima_validacion` y `puede_validar`. No va al SII.
 *
 * Los textos del backend (`MENSAJES` de `cliente/services/certificado_validacion.py`) están
 * escritos para Lemu: hablan de «la empresa» y del ejecutivo. Acá está la versión para el
 * cliente, en un solo lugar. Un código que no esté acá muestra el `mensaje` que mande el backend.
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SettingsService } from 'src/app/settings/settings.service';
import { formatearFechaCesion } from 'src/app/home/cliente-new-version/cesion-facturador/cesion-portal/cesion-portal.service';

export type EstadoCertificado = 'sin_cargar' | 'incompleto' | 'vencido' | 'sin_validar' | 'valido' | 'con_problema';

export interface PermisosSii {
  firmar: boolean;
  enviar: boolean;
}

/** `clase`: la del aviso global de `styles.scss` (`success-msg`, `error-msg`, …). */
export interface ResultadoValidacion {
  clase: 'success-msg' | 'warning-msg' | 'error-msg' | 'info-msg';
  icono: string;
  titulo: string;
  mensaje: string;
  /** Sólo cuando falta firmar o enviar: ceder exige los dos. */
  permisos: PermisosSii | null;
}

const RUTA_PERMISOS_SII = 'sii.cl → Factura electrónica → Administración → Mantención de usuarios autorizados';

/** Datos con que se completan los textos (`detalle` de la validación o `ultima_validacion`). */
interface Datos {
  vence?: string | null;
  rut_titular?: string | null;
  rut_autorizado?: string | null;
}

/** Texto largo por código: qué pasa y qué hacer. */
const MENSAJES: Record<string, (d: Datos) => string> = {
  OK: () => 'Tu certificado digital sirve para ceder tus facturas.',
  CERTIFICADO_POR_VENCER: d =>
    `Tu certificado digital sirve para ceder tus facturas, pero vence ${d.vence ? 'el ' + fechaDia(d.vence) : 'pronto'}. ` +
    'Renuévalo antes con la entidad que te lo emitió.',
  NO_FACTURA_MIPYME: () =>
    'Tu certificado digital sirve para ceder tus facturas. Como tu empresa no usa el facturador ' +
    'gratuito del SII, las cederás subiendo el archivo AEC.',
  SIN_CERTIFICADO: () => 'No tienes un certificado digital cargado. Cárgalo para poder ceder tus facturas.',
  CLAVE_CERTIFICADO_INCORRECTA: () => 'La clave no abre tu certificado digital. Vuelve a ingresarla.',
  CERTIFICADO_VENCIDO: d =>
    `Tu certificado digital venció${d.vence ? ' el ' + fechaDia(d.vence) : ''}. ` +
    'Carga uno vigente: lo renuevas con la entidad que te lo emitió.',
  SIN_RUT_AUTORIZADO: () => 'Falta el RUT del titular del certificado. Vuelve a cargar tu certificado con su clave.',
  RUT_TITULAR_NO_COINCIDE: d =>
    `Este certificado digital es de ${d.rut_titular || 'otra persona'}` +
    (d.rut_autorizado ? `, no de ${d.rut_autorizado}` : '') +
    '. Vuelve a cargarlo: el RUT se toma del certificado.',
  CERTIFICADO_NO_UTILIZABLE: () =>
    'No pudimos usar el archivo de tu certificado. Descárgalo de nuevo (archivo .pfx) y vuelve a cargarlo.',
  CERTIFICADO_RECHAZADO_SII: () =>
    'El SII no acepta este certificado digital: puede estar revocado o ser de una entidad que el SII ' +
    'no reconoce. Consigue uno vigente con tu proveedor de certificados.',
  CERTIFICADO_NO_HABILITADO_SII: () =>
    'Tu certificado digital no está habilitado para entrar al SII. Habilítalo en sii.cl ' +
    '(«Cambiar clave» → usar certificado digital) y vuelve a validarlo.',
  NO_REPRESENTA_EMPRESA: d =>
    `${d.rut_titular ? 'El RUT ' + d.rut_titular : 'El titular del certificado'} no figura como usuario ` +
    `autorizado de tu empresa en el SII. Agrégalo en ${RUTA_PERMISOS_SII}.`,
  SIN_PERMISO_FIRMA: () =>
    `Tu RUT no tiene permiso para firmar documentos en el SII de tu empresa. Dalo en ${RUTA_PERMISOS_SII}.`,
  SIN_PERMISO_ENVIO: () =>
    'Tu RUT no tiene permiso para enviar documentos en el SII de tu empresa, y para ceder se necesita ' +
    `firmar y enviar. Dalo en ${RUTA_PERMISOS_SII}.`,
};

/** Texto corto por código, para la línea de estado de «Mis credenciales». */
const MENSAJES_CORTOS: Record<string, string> = {
  CLAVE_CERTIFICADO_INCORRECTA: 'La clave no abre el certificado',
  SIN_RUT_AUTORIZADO: 'Falta el RUT del titular',
  RUT_TITULAR_NO_COINCIDE: 'El certificado es de otra persona',
  CERTIFICADO_NO_UTILIZABLE: 'El archivo no se puede usar',
  CERTIFICADO_RECHAZADO_SII: 'El SII no acepta el certificado',
  CERTIFICADO_NO_HABILITADO_SII: 'No habilitado en el SII',
  NO_REPRESENTA_EMPRESA: 'El titular no figura en tu empresa',
  SIN_PERMISO_FIRMA: 'Sin permiso para firmar en el SII',
  SIN_PERMISO_ENVIO: 'Sin permiso para enviar en el SII',
  CERTIFICADO_VENCIDO: 'Vencido',
  SIN_CERTIFICADO: 'Sin certificado',
};

/**
 * No dicen nada del certificado (tope de sesiones, SII caído, candado tomado): no cambian el
 * estado. Del backend llegan con `reintentable: true`; la lista es por si no viniera.
 */
const REINTENTABLES = new Set([
  'SII_TOPE_SESIONES', 'SII_NO_RESPONDE', 'LEMU_NO_RESPONDE', 'SII_API_NO_RESPONDE',
  'VALIDACION_EN_CURSO', 'BUSQUEDA_DTE_EN_CURSO',
]);

export const TEXTO_SIN_VERIFICAR = 'No pudimos verificarlo en el SII ahora. Puedes intentarlo más tarde.';
export const TEXTO_GUARDADO_SIN_VERIFICAR =
  'Guardamos tu certificado, pero no pudimos verificarlo en el SII ahora. Puedes intentarlo más tarde.';
export const TEXTO_TOPE_PORTAL =
  'Ya validaste tu certificado varias veces en la última hora. Inténtalo de nuevo más tarde.';

/** `"2027-05-12"` o `"12-05-2027"` → `"12/05/2027"`, sin pasar por zonas horarias. */
export function fechaDia(fecha: string | null | undefined): string {
  if (!fecha) {
    return '';
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha);
  if (iso) {
    return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(fecha);
  return dmy ? `${dmy[1]}/${dmy[2]}/${dmy[3]}` : fecha;
}

/** `dd/mm/yyyy HH:mm`, 24 h, hora de Chile. */
export function fechaHora(iso: string | null | undefined): string {
  return formatearFechaCesion(iso);
}

export function esReintentable(cuerpo: any): boolean {
  return !!cuerpo?.reintentable || REINTENTABLES.has(cuerpo?.codigo);
}

/** Texto largo para el cliente. Código desconocido: el `mensaje` del backend tal cual. */
export function mensajeCliente(codigo: string | null | undefined, mensajeBackend?: string | null, datos: Datos = {}): string {
  const fn = codigo ? MENSAJES[codigo] : undefined;
  if (fn) {
    return fn(datos);
  }
  return mensajeBackend || 'No pudimos validar tu certificado digital.';
}

export function mensajeCortoCliente(codigo: string | null | undefined, cortoBackend?: string | null): string {
  return (codigo && MENSAJES_CORTOS[codigo]) || cortoBackend || 'Con problemas en el SII';
}

/** Sólo si falta firmar o enviar. `null` si no vino (no se llegó al SII) o están los dos. */
export function permisosFaltantes(permisos: any): PermisosSii | null {
  if (!permisos || typeof permisos !== 'object') {
    return null;
  }
  const firmar = !!permisos.firmar;
  const enviar = !!permisos.enviar;
  return firmar && enviar ? null : { firmar, enviar };
}

/**
 * El resultado de un `POST validar` (200) para mostrar en el diálogo, después de guardar. `null` si es reintentable: ahí se muestra
 * `TEXTO_SIN_VERIFICAR` o `TEXTO_GUARDADO_SIN_VERIFICAR` y no cambia nada.
 */
export function resultadoValidacion(cuerpo: any): ResultadoValidacion | null {
  if (!cuerpo || esReintentable(cuerpo)) {
    return null;
  }
  const detalle = cuerpo.detalle || {};
  const datos: Datos = { vence: detalle.vence, rut_titular: detalle.rut_titular, rut_autorizado: detalle.rut_autorizado };
  const mensaje = mensajeCliente(cuerpo.codigo, cuerpo.mensaje, datos);
  if (cuerpo.ok) {
    const aviso = cuerpo.codigo && cuerpo.codigo !== 'OK';
    return {
      clase: aviso ? 'warning-msg' : 'success-msg',
      icono: 'verified',
      titulo: 'Guardamos tu certificado y es válido en el SII',
      mensaje,
      permisos: null,
    };
  }
  return {
    clase: 'error-msg',
    icono: 'error',
    titulo: 'Guardamos tu certificado, pero todavía no sirve para ceder',
    mensaje,
    permisos: permisosFaltantes(cuerpo.permisos ?? detalle.permisos),
  };
}

/** Texto para un `POST validar` que no llegó a dar resultado (409, 503, 429, red). */
export function textoErrorValidar(bd: any, despuesDeGuardar: boolean): string {
  if (bd?.status === 429) {
    return despuesDeGuardar
      ? 'Guardamos tu certificado, pero ya lo validaste varias veces en la última hora. Puedes verificarlo más tarde.'
      : TEXTO_TOPE_PORTAL;
  }
  return despuesDeGuardar ? TEXTO_GUARDADO_SIN_VERIFICAR : TEXTO_SIN_VERIFICAR;
}

/** Línea de estado de «Mis credenciales», desde `success.certificado`. */
export function estadoLinea(certificado: any): { clase: string; icono: string; texto: string } {
  const ultima = certificado?.ultima_validacion;
  switch (certificado?.estado as EstadoCertificado) {
    case 'valido':
      return {
        clase: 'estado-ok', icono: 'verified',
        texto: ultima?.validado_en ? `Válido en el SII · verificado el ${fechaHora(ultima.validado_en)}` : 'Válido en el SII',
      };
    case 'vencido':
      return { clase: 'estado-error', icono: 'event_busy', texto: certificado?.vence ? `Venció el ${fechaDia(certificado.vence)}` : 'Vencido' };
    case 'con_problema':
      return { clase: 'estado-error', icono: 'error', texto: mensajeCortoCliente(ultima?.codigo, ultima?.mensaje_corto) };
    case 'incompleto': {
      const falta: string[] = certificado?.falta || [];
      const texto = falta.includes('clave') ? 'Falta la clave del certificado'
        : falta.includes('rut_autorizado') ? 'Falta el RUT del titular'
        : 'Falta completar el certificado';
      return { clase: 'estado-alerta', icono: 'error_outline', texto };
    }
    case 'sin_validar':
      return { clase: 'estado-neutro', icono: 'help', texto: 'Sin verificar en el SII' };
    case 'sin_cargar':
      return { clase: 'estado-neutro', icono: 'upload_file', texto: 'Sin certificado cargado' };
    default:
      return { clase: 'estado-neutro', icono: 'help', texto: '' };
  }
}

/**
 * `POST validar` con `origen: "portal"`. Va con `HttpClient` y no con `SettingsService.get_query`,
 * como el panel de cesión: `get_query` cierra la sesión ante un 403 y abre el diálogo de error
 * ante un 500, y acá un 409 / 503 / 429 sólo significa «ahora no se pudo».
 *
 * Nunca se llama al abrir una pantalla: cada validación es un login en el SII con el certificado.
 */
@Injectable({ providedIn: 'root' })
export class CertificadoValidacionService {
  constructor(private http: HttpClient) {}

  async validar(companyId: string | number): Promise<{ status: number; body: any }> {
    const url = `${SettingsService.API_ENDPOINT}cliente/certificado-digital/${companyId}/validar/`;
    const headers = new HttpHeaders().set('Authorization', 'JWT ' + (localStorage.getItem('token') || ''));
    try {
      const respuesta: any = await firstValueFrom(this.http.post(url, { origen: 'portal' }, { headers, observe: 'response' }));
      return { status: respuesta.status, body: respuesta.body || {} };
    } catch (error: any) {
      const cuerpo = error?.error;
      return { status: error?.status ?? 0, body: cuerpo && typeof cuerpo === 'object' ? cuerpo : {} };
    }
  }
}
